import { type DBSchema, type IDBPDatabase, openDB } from 'idb';
import log from 'loglevel';

import { INDEXED_DB_WRITE_COALESCE_MS, WriteCoalescer } from './writeCoalescer';
import { MentionPickerContact } from '../../types/mentionPickerContact';

export const CONTACT_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
export const MAX_CONTACT_CACHE_ENTRIES_PER_USER = 500;

type DeletedItemEntityType = 'inbox-entry' | 'user-communication';

export interface DeletedItemEntry {
  id: string;
  deletedAt: number;
  entityType: DeletedItemEntityType;
  version?: number | null;
}

interface ContactCacheEntry {
  cacheKey: string;
  contact: MentionPickerContact;
  lastFetchedAt: number;
  lastSelectedAt?: number;
}

interface AppDBSchema extends DBSchema {
  'zustand-state': {
    key: string;
    value: any;
  };
  'deleted-items': {
    key: string;
    value: DeletedItemEntry;
  };
  'contact-cache': {
    key: string;
    value: ContactCacheEntry;
  };
}

const DELETED_ITEMS_CLEANUP_MS = 24 * 60 * 60 * 1000;
const DEFAULT_DB_OPEN_TIMEOUT_MS = 3000;

export class IndexedDBTimeoutError extends Error {
  constructor() {
    super('IndexedDB connection timed out');
    this.name = 'IndexedDBTimeoutError';
  }
}

const isVersionError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { name?: string }).name === 'VersionError';

export class IndexedDBAdapter {
  // Nullable: `blocking`/`terminated` null this out so the next call reopens
  // lazily instead of reusing a closed/stale connection.
  private dbPromise: Promise<IDBPDatabase<AppDBSchema>> | null;
  private dbName: string;
  private version: number;
  private pendingWrites: Map<string, Promise<void>> = new Map();
  private coalescer: WriteCoalescer<string> = new WriteCoalescer<string>({
    delayMs: INDEXED_DB_WRITE_COALESCE_MS,
    write: (name, value) => this.trackedWrite(name, value),
  });

  constructor(dbName = 'zustand-storage', version = 4) {
    this.dbName = dbName;
    this.version = version;
    this.dbPromise = this.openWithVersionFallback();
  }

  private openDbConnection(
    version?: number,
  ): Promise<IDBPDatabase<AppDBSchema>> {
    return openDB<AppDBSchema>(this.dbName, version, {
      upgrade(db, oldVersion, _newVersion, transaction) {
        if (!db.objectStoreNames.contains('zustand-state')) {
          db.createObjectStore('zustand-state');
        }
        if (!db.objectStoreNames.contains('deleted-items')) {
          db.createObjectStore('deleted-items');
        } else if (oldVersion < 4) {
          // Pre-v4 tombstones were written on every removal (including moves),
          // suppressing legitimate re-inserts for 24h. Clear them so version-scoped
          // tombstoning starts from a clean slate.
          transaction.objectStore('deleted-items').clear();
        }
        if (!db.objectStoreNames.contains('contact-cache')) {
          db.createObjectStore('contact-cache');
        }
      },
      blocked: (currentVersion, blockedVersion) => {
        // An older tab/build still holds a v{currentVersion} connection open, so
        // the upgrade transaction can't start — it stays pending until that tab
        // closes. Without getDb()'s timeout, every caller here would hang forever.
        log.warn(
          `[IndexedDBAdapter] Upgrade to v${blockedVersion} blocked by an existing v${currentVersion} connection open in another tab.`,
        );
      },
      blocking: (currentVersion, blockedVersion) => {
        log.warn(
          `[IndexedDBAdapter] This v${currentVersion} connection is blocking another tab's upgrade to v${blockedVersion}; closing it.`,
        );
        this.invalidateConnection();
      },
      terminated: () => {
        log.warn(
          '[IndexedDBAdapter] Connection was terminated unexpectedly; will reopen on next use.',
        );
        this.dbPromise = null;
      },
    });
  }

  private invalidateConnection(): void {
    const staleDbPromise = this.dbPromise;
    this.dbPromise = null;
    staleDbPromise
      ?.then((db) => db.close())
      .catch((error) => {
        log.error('Error closing stale IndexedDB connection:', error);
      });
  }

  private async openWithVersionFallback(): Promise<IDBPDatabase<AppDBSchema>> {
    try {
      return await this.openDbConnection(this.version);
    } catch (error) {
      if (!isVersionError(error)) {
        throw error;
      }
      // Another tab already upgraded the DB past our hardcoded version (e.g. a
      // future v5) — reopen at whatever version currently exists; our object
      // stores are already present there, so no explicit version is needed.
      log.warn(
        '[IndexedDBAdapter] Requested version is behind the current DB version; reopening without an explicit version.',
      );
      return this.openDbConnection();
    }
  }

  // Every public method must go through this instead of awaiting dbPromise
  // directly: a blocked upgrade (another tab holding an old connection open)
  // leaves dbPromise pending indefinitely, which would otherwise hang zustand
  // persist hydration and freeze the app on the boot skeleton. Racing a timeout
  // only rejects this call's wrapper — dbPromise itself is left untouched, so
  // once the blocking tab closes and the upgrade completes, later calls succeed.
  private getDb(
    timeoutMs = DEFAULT_DB_OPEN_TIMEOUT_MS,
  ): Promise<IDBPDatabase<AppDBSchema>> {
    if (!this.dbPromise) {
      this.dbPromise = this.openWithVersionFallback();
    }
    const dbPromise = this.dbPromise;

    return new Promise<IDBPDatabase<AppDBSchema>>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new IndexedDBTimeoutError());
      }, timeoutMs);

      dbPromise.then(
        (db) => {
          clearTimeout(timer);
          resolve(db);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        },
      );
    });
  }

  async getItem(name: string): Promise<string | null> {
    const pendingValue = this.coalescer.peek(name);
    if (pendingValue !== undefined) {
      return pendingValue;
    }

    try {
      const db = await this.getDb();
      const value = await db.get('zustand-state', name);
      return value ? JSON.stringify(value) : null;
    } catch (error) {
      log.error('Error getting item from IndexedDB:', error);
      return null;
    }
  }

  async setItem(name: string, value: string): Promise<void> {
    this.coalescer.schedule(name, value);
  }

  private trackedWrite(name: string, value: string): Promise<void> {
    const writePromise = this.performWrite(name, value);
    this.pendingWrites.set(name, writePromise);

    return writePromise.finally(() => {
      this.pendingWrites.delete(name);
    });
  }

  private async performWrite(name: string, value: string): Promise<void> {
    try {
      const db = await this.getDb();
      const parsedValue = JSON.parse(value);

      const tx = db.transaction('zustand-state', 'readwrite');
      await tx.store.put(parsedValue, name);
      await tx.done;
    } catch (error) {
      log.error('Error setting item in IndexedDB:', error);
      throw error;
    }
  }

  async removeItem(name: string): Promise<void> {
    // Cancel first so no *new* write starts, then drain the write already in
    // flight (its timer fired, `cancel` can no longer stop it) before deleting —
    // otherwise a `put` that started before this call wins the race and
    // resurrects the cleared state.
    this.coalescer.cancel(name);
    await this.pendingWrites.get(name)?.catch(() => undefined);
    try {
      const db = await this.getDb();
      await db.delete('zustand-state', name);
    } catch (error) {
      log.error('Error removing item from IndexedDB:', error);
    }
  }

  async markAsDeleted(
    id: string,
    entityType: DeletedItemEntityType,
    version?: number | null,
  ): Promise<void> {
    await this.markManyAsDeleted([{ id, entityType, version }]);
  }

  async markManyAsDeleted(
    entries: ReadonlyArray<{
      id: string;
      entityType: DeletedItemEntityType;
      version?: number | null;
    }>,
  ): Promise<void> {
    if (entries.length === 0) {
      return;
    }
    try {
      const db = await this.getDb();
      const deletedAt = Date.now();
      const tx = db.transaction('deleted-items', 'readwrite');
      await Promise.all(
        entries.map(({ id, entityType, version }) =>
          tx.store.put(
            {
              id,
              deletedAt,
              entityType,
              version: version ?? null,
            },
            id,
          ),
        ),
      );
      await tx.done;
    } catch (error) {
      log.error('Error marking items as deleted:', error);
    }
  }

  async isDeletedLocally(id: string): Promise<boolean> {
    try {
      const db = await this.getDb();
      const entry = await db.get('deleted-items', id);
      return !!entry;
    } catch (error) {
      log.error('Error checking deleted status:', error);
      return false;
    }
  }

  async getDeletedItem(id: string): Promise<DeletedItemEntry | undefined> {
    try {
      const db = await this.getDb();
      return await db.get('deleted-items', id);
    } catch (error) {
      log.error('Error getting deleted item:', error);
      return undefined;
    }
  }

  async unmarkAsDeleted(id: string): Promise<void> {
    try {
      const db = await this.getDb();
      await db.delete('deleted-items', id);
    } catch (error) {
      log.error('Error unmarking item as deleted:', error);
    }
  }

  async cleanupDeletedItems(
    maxAgeMs = DELETED_ITEMS_CLEANUP_MS,
  ): Promise<void> {
    try {
      const db = await this.getDb();
      const tx = db.transaction('deleted-items', 'readwrite');
      const cutoff = Date.now() - maxAgeMs;

      let cursor = await tx.store.openCursor();
      while (cursor) {
        if (cursor.value.deletedAt < cutoff) {
          await cursor.delete();
        }
        cursor = await cursor.continue();
      }
      await tx.done;
    } catch (error) {
      log.error('Error cleaning up deleted items:', error);
    }
  }

  async getCachedContacts(userId: string): Promise<ContactCacheEntry[]> {
    try {
      const db = await this.getDb();
      const allEntries = await db.getAll('contact-cache');
      const userPrefix = `${userId}:`;
      return allEntries.filter((e) => e.cacheKey.startsWith(userPrefix));
    } catch (error) {
      log.error('Error getting cached contacts from IndexedDB:', error);
      return [];
    }
  }

  async saveCachedContact(
    userId: string,
    contact: MentionPickerContact,
    type: 'fetched' | 'selected',
  ): Promise<void> {
    try {
      const db = await this.getDb();
      const key = `${userId}:${contact.value}`;
      const existing = await db.get('contact-cache', key);
      const now = Date.now();
      const entry: ContactCacheEntry = {
        cacheKey: key,
        contact,
        lastFetchedAt:
          type === 'fetched' ? now : (existing?.lastFetchedAt ?? now),
        lastSelectedAt: type === 'selected' ? now : existing?.lastSelectedAt,
      };
      const tx = db.transaction('contact-cache', 'readwrite');
      await tx.store.put(entry, key);
      await tx.done;
    } catch (error) {
      log.error('Error saving cached contact to IndexedDB:', error);
    }
  }

  async saveCachedContactsBatch(
    userId: string,
    contacts: MentionPickerContact[],
    type: 'fetched' | 'selected',
  ): Promise<void> {
    if (contacts.length === 0) {
      return;
    }
    try {
      const db = await this.getDb();
      const now = Date.now();
      const tx = db.transaction('contact-cache', 'readwrite');
      await Promise.all(
        contacts.map(async (contact) => {
          const key = `${userId}:${contact.value}`;
          const existing = await tx.store.get(key);
          const entry: ContactCacheEntry = {
            cacheKey: key,
            contact,
            lastFetchedAt:
              type === 'fetched' ? now : (existing?.lastFetchedAt ?? now),
            lastSelectedAt:
              type === 'selected' ? now : existing?.lastSelectedAt,
          };
          return tx.store.put(entry, key);
        }),
      );
      await tx.done;
    } catch (error) {
      log.error('Error saving cached contacts batch to IndexedDB:', error);
    }
  }

  async clearExpiredContactCache(
    maxAgeMs = CONTACT_CACHE_TTL_MS,
  ): Promise<void> {
    try {
      const db = await this.getDb();

      // Phase 1: remove entries older than TTL
      const expiredTx = db.transaction('contact-cache', 'readwrite');
      const cutoff = Date.now() - maxAgeMs;
      let cursor = await expiredTx.store.openCursor();
      while (cursor) {
        const lastActivity = Math.max(
          cursor.value.lastFetchedAt,
          cursor.value.lastSelectedAt ?? 0,
        );
        if (lastActivity < cutoff) {
          await cursor.delete();
        }
        cursor = await cursor.continue();
      }
      await expiredTx.done;

      // Phase 2: enforce per-user entry cap (LRU — evict oldest first)
      const allEntries = await db.getAll('contact-cache');
      const byUser = new Map<string, ContactCacheEntry[]>();
      for (const entry of allEntries) {
        const userId = entry.cacheKey.split(':')[0];
        const list = byUser.get(userId) ?? [];
        list.push(entry);
        byUser.set(userId, list);
      }

      const capTx = db.transaction('contact-cache', 'readwrite');
      for (const entries of byUser.values()) {
        if (entries.length <= MAX_CONTACT_CACHE_ENTRIES_PER_USER) {
          continue;
        }
        entries.sort((a, b) => {
          const aActivity = Math.max(a.lastFetchedAt, a.lastSelectedAt ?? 0);
          const bActivity = Math.max(b.lastFetchedAt, b.lastSelectedAt ?? 0);
          return aActivity - bActivity;
        });
        const toDelete = entries.slice(
          0,
          entries.length - MAX_CONTACT_CACHE_ENTRIES_PER_USER,
        );
        for (const entry of toDelete) {
          await capTx.store.delete(entry.cacheKey);
        }
      }
      await capTx.done;
    } catch (error) {
      log.error('Error clearing expired contact cache from IndexedDB:', error);
    }
  }

  async flush(): Promise<void> {
    await this.coalescer.flush();
    await Promise.all(this.pendingWrites.values());
  }
}

export const indexedDBAdapter = new IndexedDBAdapter();

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    void indexedDBAdapter.flush();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void indexedDBAdapter.flush();
    }
  });
}

export const createIndexedDBStorage = () => {
  return {
    getItem: indexedDBAdapter.getItem.bind(indexedDBAdapter),
    setItem: indexedDBAdapter.setItem.bind(indexedDBAdapter),
    removeItem: indexedDBAdapter.removeItem.bind(indexedDBAdapter),
  };
};
